/**
 * DebugReviewPanel component.
 *
 * Specialised renderer for attention items with review_type: "debug_review".
 * Renders the agent's diagnostic report (body_text) as formatted markdown-style
 * content with monospace code sections, copy actions, and an execution deep-link.
 *
 * Task 2631.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Bug, Copy, ExternalLink, Send, Check } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui'
import type { AttentionItem } from '../types'
import { getCustomerDisplay } from '../types'

export interface DebugReviewPanelProps {
  /** The attention item being reviewed. */
  item: AttentionItem
  /** Whether a resolution action is pending. */
  actionPending?: boolean
  /** Called when the operator resolves with a reply. */
  onResolve?: (reply: string) => void
  /** Called when the operator dismisses the item. */
  onDismiss?: () => void
}

/**
 * Extract the debug review fields from item.details.
 * The backend stores these fields inside details.proposed_resolution.
 */
function getDebugFields(item: AttentionItem) {
  const details = item.details ?? {}
  const proposedResolution = (details.proposed_resolution as Record<string, unknown>) ?? {}
  return {
    title: (proposedResolution.title as string) ?? (details.title as string) ?? item.title,
    bodyText: (proposedResolution.body_text as string) ?? '',
    reviewType: (proposedResolution.review_type as string) ?? '',
    reviewUrl: (proposedResolution.review_url as string) ?? '',
  }
}

/**
 * Render markdown-style body text with basic formatting.
 * Handles headings (#), code blocks (```), and paragraphs.
 */
function renderBodyText(text: string): React.ReactNode[] {
  if (!text) return []

  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  let inCodeBlock = false
  let codeLines: string[] = []
  let key = 0

  for (const line of lines) {
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={key++} style={bodyStyles.codeBlock}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        )
        codeLines = []
        inCodeBlock = false
      } else {
        inCodeBlock = true
      }
      continue
    }

    if (inCodeBlock) {
      codeLines.push(line)
      continue
    }

    if (line.startsWith('# ')) {
      elements.push(<h3 key={key++} style={bodyStyles.h3}>{line.slice(2)}</h3>)
    } else if (line.startsWith('## ')) {
      elements.push(<h4 key={key++} style={bodyStyles.h4}>{line.slice(3)}</h4>)
    } else if (line.startsWith('### ')) {
      elements.push(<h5 key={key++} style={bodyStyles.h5}>{line.slice(4)}</h5>)
    } else if (line.startsWith('- ')) {
      elements.push(
        <div key={key++} style={bodyStyles.listItem}>
          <span style={bodyStyles.bullet}>•</span>
          <span>{line.slice(2)}</span>
        </div>
      )
    } else if (line.trim() === '') {
      elements.push(<div key={key++} style={{ height: '8px' }} />)
    } else {
      elements.push(<p key={key++} style={bodyStyles.paragraph}>{line}</p>)
    }
  }

  // Close any unclosed code block
  if (inCodeBlock && codeLines.length > 0) {
    elements.push(
      <pre key={key++} style={bodyStyles.codeBlock}>
        <code>{codeLines.join('\n')}</code>
      </pre>
    )
  }

  return elements
}

/**
 * Debug review panel for system_debugger agent reports.
 */
export function DebugReviewPanel({
  item,
  actionPending,
  onResolve,
  onDismiss,
}: DebugReviewPanelProps) {
  const [reply, setReply] = React.useState('')
  const [copied, setCopied] = React.useState(false)
  const { title, bodyText, reviewUrl } = getDebugFields(item)
  const customer = getCustomerDisplay(item)

  const handleSubmit = React.useCallback(() => {
    if (reply.trim() && onResolve) {
      onResolve(reply.trim())
    }
  }, [reply, onResolve])

  const handleCopyReport = React.useCallback(async () => {
    if (!bodyText) return
    try {
      await navigator.clipboard.writeText(bodyText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [bodyText])

  return (
    <div style={styles.container}>
      <Card>
        <CardHeader style={styles.header}>
          <div style={styles.headerRow}>
            <Badge
              variant="outline"
              style={{ borderColor: 'var(--warning)', color: 'var(--warning)' }}
            >
              <Bug style={{ width: '12px', height: '12px', marginRight: '4px' }} />
              Debug Review
            </Badge>
            <CardTitle style={styles.title}>{title}</CardTitle>
          </div>
          {customer && (
            <span style={styles.customer}>{customer}</span>
          )}
        </CardHeader>
        <CardContent>
          {/* Agent name */}
          {item.agent_name && (
            <div style={styles.agentRow}>
              <span style={styles.agentLabel}>Agent:</span>
              <span style={styles.agentName}>{item.agent_name}</span>
            </div>
          )}

          {/* Diagnostic report */}
          <div style={styles.reportContainer}>
            <div style={styles.reportHeader}>
              <span style={styles.reportTitle}>Investigation Report</span>
              <div style={styles.reportActions}>
                <button style={styles.copyButton} onClick={handleCopyReport} disabled={!bodyText}>
                  {copied ? (
                    <><Check style={{ width: '12px', height: '12px' }} /> Copied</>
                  ) : (
                    <><Copy style={{ width: '12px', height: '12px' }} /> Copy</>
                  )}
                </button>
              </div>
            </div>
            <div style={styles.reportBody}>
              {renderBodyText(bodyText)}
            </div>
          </div>

          {/* Execution link */}
          {(reviewUrl || item.execution_id) && (
            <a
              href={reviewUrl || `/executions/${item.execution_id}/audit`}
              style={styles.executionLink}
              target={reviewUrl ? '_blank' : undefined}
              rel={reviewUrl ? 'noopener noreferrer' : undefined}
            >
              <ExternalLink style={{ width: '14px', height: '14px' }} />
              Open execution
            </a>
          )}

          {/* Reply area */}
          <div style={styles.replySection}>
            <span style={styles.replyLabel}>Your reply</span>
            <textarea
              style={styles.textarea}
              placeholder="Enter your reply or remediation notes..."
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
            />
          </div>

          {/* Action buttons */}
          <div style={styles.actions}>
            <Button
              variant="default"
              disabled={!reply.trim() || actionPending}
              onClick={handleSubmit}
            >
              <Send style={{ width: '14px', height: '14px', marginRight: '6px' }} />
              {actionPending ? 'Sending...' : 'Reply & Resolve'}
            </Button>
            <Button
              variant="ghost"
              disabled={actionPending}
              onClick={onDismiss}
            >
              Dismiss
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  header: { display: 'flex', flexDirection: 'column', gap: '8px' },
  headerRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  title: { fontSize: '16px', fontWeight: 600 },
  customer: { fontSize: '12px', color: 'var(--text-muted)' },
  agentRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', fontSize: '12px' },
  agentLabel: { color: 'var(--text-muted)' },
  agentName: { fontWeight: 500, color: 'var(--text-secondary)' },
  reportContainer: {
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-tertiary)',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  reportHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'rgba(245, 158, 11, 0.05)',
  },
  reportTitle: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em', color: 'var(--warning)' },
  reportActions: { display: 'flex', gap: '8px' },
  copyButton: {
    display: 'flex', alignItems: 'center', gap: '4px',
    fontSize: '11px', color: 'var(--text-muted)',
    background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
    borderRadius: '4px',
    transition: 'background-color 150ms, color 150ms',
  },
  reportBody: { padding: '12px', maxHeight: '400px', overflow: 'auto' },
  executionLink: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontSize: '13px', color: 'var(--accent-primary)', textDecoration: 'none',
    marginBottom: '16px',
  },
  replySection: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  replyLabel: { fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' },
  textarea: {
    width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)', background: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.5,
    resize: 'vertical' as const, fontFamily: 'inherit', outline: 'none',
  },
  actions: { display: 'flex', alignItems: 'center', gap: '8px' },
}

const bodyStyles: Record<string, CSSProperties> = {
  h3: { fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px 0' },
  h4: { fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: '8px 0 4px 0' },
  h5: { fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '8px 0 4px 0' },
  paragraph: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 4px 0' },
  listItem: { display: 'flex', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginLeft: '4px' },
  bullet: { color: 'var(--text-muted)', flexShrink: 0 },
  codeBlock: {
    padding: '10px 12px', borderRadius: 'var(--radius-sm)',
    background: 'rgba(0, 0, 0, 0.3)', border: '1px solid var(--border-subtle)',
    fontSize: '12px', fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const, margin: '4px 0 8px 0', overflow: 'auto',
  },
}

export default DebugReviewPanel
